/**
 * Page /admin/lieux — Référentiel de lieux communaux (socle S4-a, mig 203).
 *
 * ─── LE BESOIN ──────────────────────────────────────────────────────────────
 *
 * VPC portait deux référentiels de bâtiments (écoles, structures PE) et aucun
 * pour les salles — rangées à côté des tronçonneuses. Cet écran pose le
 * référentiel commun : tous les bâtiments communaux, d'un seul endroit.
 *
 * ─── LES DEUX PRINCIPES ─────────────────────────────────────────────────────
 *
 * · Un lieu se saisit UNE fois, puis se choisit partout (l'unicité normalisée
 *   côté serveur refuse « Ecole du centre » quand « École du Centre » existe).
 * · Les types sont un BAC À SABLE : la commune renomme, désactive, en crée.
 *   VPC propose, la commune dispose.
 *
 * Gestion (créer/modifier) réservée à l'admin ; la lecture est ouverte au
 * manager (il rattache ses plannings/réservations à un lieu).
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException, type LieuDTO, type LieuTypeDTO, type LieuInput, type OccupantLieuDTO } from '@/lib/api';
import type { CleBatiment } from '@roronoa-collectivites/shared-types';
import { MapPin, Loader2, Plus, Pencil, Tag, Building2, Users, Home, KeyRound } from 'lucide-react';
import type { BatimentContrat } from '@roronoa-collectivites/shared-types';

/** Date de calendrier en JJ/MM/AAAA. Aucun horodatage, donc aucun décalage. */
function dateFr(iso: string | null): string {
  return iso ? iso.split('-').reverse().join('/') : '—';
}

const SELECT_CLS =
  'h-10 w-full rounded-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
const TEXTAREA_CLS =
  'w-full rounded-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

/** Libellés métier des briques — les clés techniques ne parlent pas à un maire. */
const BRIQUE_LABEL: Record<string, string> = {
  grc: 'Relation citoyen (GRC)',
  service_technique: 'Service technique',
  scolaire_cantine: 'Scolaire & cantine',
  associations: 'Associations',
  urbanisme: 'Urbanisme',
  comptable: 'Comptabilité',
  petite_enfance: 'Petite enfance',
  rh: 'Ressources humaines',
};

const emptyForm: LieuInput = {
  nom: '', typeId: null, adresse: '', codePostal: '', ville: '',
  categorieErp: null, typeErp: '', capaciteErp: null, surfaceM2: null,
  louable: false, notes: '', briques: [],
};

export function LieuxPage() {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  // 9.2 — message de retour après déclaration des clés, par lieu.
  /**
   * Les clés déclarées, par lieu — chargées UNE fois au montage.
   *
   * Deux tentatives ratées avant celle-ci : un message d'écran (perdu à la
   * navigation), puis un chargement au dépliage de la fiche (invisible tant
   * qu'on ne dépliait pas). La liste des clés se lit d'un seul appel pour toute
   * la commune : autant la charger avec la page.
   */
  const [clesParLieu, setClesParLieu] = useState<Record<string, CleBatiment[]>>({});
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const [types, setTypes] = useState<LieuTypeDTO[]>([]);
  const [briques, setBriques] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [inclureInactifs, setInclureInactifs] = useState(false);

  // Édition d'un lieu (null = fermé, '' = création, id = édition).
  const [editLieu, setEditLieu] = useState<string | null>(null);
  const [form, setForm] = useState<LieuInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  // Gestion des types (bac à sable).
  const [typesOpen, setTypesOpen] = useState(false);
  const [nouveauType, setNouveauType] = useState('');

  // Occupants dépliés par lieu (S4-b) — « ce bâtiment abrite… ».
  const [occupants, setOccupants] = useState<Record<string, OccupantLieuDTO[] | 'loading'>>({});
  // Occupations mairie (vague 4d, mig 247) : quand un projet réserve le lieu.
  type OccupationLieu = { id: string; dateDebut: string; dateFin: string; motif: string; sourceType: string };
  const [occupations, setOccupations] = useState<Record<string, OccupationLieu[]>>({});
  /**
   * Les contrats du bâtiment (item 7.5, mig 271).
   *
   * La fiche lieu AFFICHE les contrats, elle ne les porte pas : un bâtiment en a
   * N simultanés (bail + chauffage + assurance), des colonnes sur `lieux` n'en
   * tiendraient qu'un. Même principe que les occupations.
   *
   * Chargement séparé et TOLÉRANT À L'ÉCHEC : la route est gardée par les
   * permissions du parc bâtimentaire, qu'un admin de référentiel n'a pas
   * forcément. Un 403 ne doit pas casser l'écran des lieux.
   */
  const [contrats, setContrats] = useState<Record<string, BatimentContrat[]>>({});

  /**
   * 9.2 — déclarer les clés d'un lieu en une saisie.
   *
   * Une ligne par clé plutôt qu'un nombre : ce qu'on veut retenir, c'est le
   * repère porté sur le trousseau (« A-12 », « porte-clé jaune 3 »), pas la
   * quantité. Une ligne vide reste une clé — toutes les communes ne marquent
   * pas leurs exemplaires.
   */
  const declarerCles = async (l: LieuDTO) => {
    // Montrer ce qui existe DÉJÀ : sans ça, on redéclare « cle 01 » une seconde
    // fois sans s'en apercevoir, et on se retrouve avec deux exemplaires du même
    // numéro (constaté le 31/07).
    const dejaLa = clesParLieu[l.id] ?? [];
    const rappel = dejaLa.length === 0
      ? 'Aucune clé déclarée pour ce lieu à ce jour.'
      : `Déjà déclarées : ${dejaLa
          .map((c) => (c.numeroGrave?.trim() ? `n° ${c.numeroGrave}` : `exemplaire ${c.exemplaire}`))
          .join(' · ')}.`;

    const saisie = await prompt({
      title: `Clés de ${l.nom}`,
      description:
        `${rappel}\n\nAjoutez une clé par ligne. Indiquez le numéro gravé ou le `
        + 'repère du porte-clé ; laissez vide si la clé ne porte aucune marque.',
      multiline: true,
      confirmLabel: 'Déclarer',
    });
    if (saisie === null) return;
    const numeros = saisie.split('\n').map((x) => x.trim()).filter((x, i, a) =>
      x !== '' || a.length === 1);
    if (numeros.length === 0) {
      setError('Aucune clé saisie.');
      return;
    }
    try {
      const r = await api.admin.lieux.declarerClesDuLieu(l.id, numeros);
      // On repart de la réponse du serveur plutôt que d'additionner ce qu'on
      // vient d'envoyer : c'est la seule valeur qui ne peut pas mentir.
      setClesParLieu((m) => ({
        ...m, [l.id]: r.portes.flatMap((p) => p.cles ?? []),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Déclaration impossible.');
    }
  };

  const toggleOccupants = async (lieuId: string) => {
    if (occupants[lieuId] && occupants[lieuId] !== 'loading') {
      setOccupants((o) => { const n = { ...o }; delete n[lieuId]; return n; });
      return;
    }
    setOccupants((o) => ({ ...o, [lieuId]: 'loading' }));
    try {
      const r = await api.admin.lieux.occupants(lieuId);
      setOccupants((o) => ({ ...o, [lieuId]: r.occupants }));
      setOccupations((o) => ({ ...o, [lieuId]: r.occupations ?? [] }));
      try {
        const rc = await api.admin.lieux.contratsDuLieu(lieuId);
        setContrats((c) => ({ ...c, [lieuId]: rc.contrats }));
      } catch { /* sans droit sur les contrats : le reste de la fiche reste lisible */ }
    } catch {
      setOccupants((o) => { const n = { ...o }; delete n[lieuId]; return n; });
    }
  };

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [rl, rt, rb] = await Promise.all([
        api.admin.lieux.list(inclureInactifs),
        api.admin.lieux.types.list(),
        api.admin.lieux.briques(),
      ]);
      setLieux(rl.lieux);
      setTypes(rt.types);
      setBriques(rb.briques);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [inclureInactifs]);

  useEffect(() => { void charger(); }, [charger]);

  // Les clés de toute la commune, en un appel. Sans droit sur les clés, la page
  // reste parfaitement lisible : le bloc disparaît, rien ne casse.
  useEffect(() => {
    let vivant = true;
    api.admin.lieux.cles()
      .then((r) => {
        if (!vivant) return;
        const parLieu: Record<string, CleBatiment[]> = {};
        for (const p of r.portes) {
          (parLieu[p.lieuId] ??= []).push(...(p.cles ?? []));
        }
        setClesParLieu(parLieu);
      })
      .catch(() => { /* pas de droit clés : on n'affiche simplement pas le bloc */ });
    return () => { vivant = false; };
  }, []);

  const flash = (m: string) => { setMsg(m); window.setTimeout(() => setMsg(null), 3500); };

  const ouvrirCreation = () => { setForm(emptyForm); setEditLieu(''); };
  const ouvrirEdition = (l: LieuDTO) => {
    setForm({
      nom: l.nom, typeId: l.typeId, adresse: l.adresse ?? '', codePostal: l.codePostal ?? '',
      ville: l.ville ?? '', categorieErp: l.categorieErp, typeErp: l.typeErp ?? '',
      capaciteErp: l.capaciteErp, surfaceM2: l.surfaceM2, louable: l.louable,
      notes: l.notes ?? '', briques: l.briques,
    });
    setEditLieu(l.id);
  };

  const estErp = form.categorieErp != null;

  const enregistrer = async () => {
    if (!form.nom.trim()) { setError('Le nom du lieu est obligatoire.'); return; }
    setSaving(true);
    try {
      // On n'envoie la capacité ERP que si le lieu est déclaré ERP.
      const payload: LieuInput = {
        ...form,
        nom: form.nom.trim(),
        capaciteErp: estErp ? form.capaciteErp : null,
        typeErp: estErp ? form.typeErp : null,
      };
      if (editLieu === '') {
        await api.admin.lieux.create(payload);
        flash('Lieu créé.');
      } else if (editLieu) {
        await api.admin.lieux.update(editLieu, payload);
        flash('Lieu mis à jour.');
      }
      setEditLieu(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const basculerActif = async (l: LieuDTO) => {
    const ok = await confirm({
      title: l.actif ? 'Désactiver ce lieu ?' : 'Réactiver ce lieu ?',
      description: l.actif
        ? `« ${l.nom} » n'apparaîtra plus dans les listes de sélection. Les rattachements existants sont conservés.`
        : `« ${l.nom} » réapparaîtra dans les listes de sélection.`,
      danger: l.actif,
    });
    if (!ok) return;
    try {
      await api.admin.lieux.update(l.id, { actif: !l.actif });
      flash(l.actif ? 'Lieu désactivé.' : 'Lieu réactivé.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Action impossible.');
    }
  };

  const creerType = async () => {
    const lib = nouveauType.trim();
    if (!lib) return;
    try {
      await api.admin.lieux.types.create({ libelle: lib });
      setNouveauType('');
      const rt = await api.admin.lieux.types.list();
      setTypes(rt.types);
      flash('Type ajouté.');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Type impossible à créer.');
    }
  };

  const basculerType = async (t: LieuTypeDTO) => {
    try {
      await api.admin.lieux.types.update(t.id, { actif: !t.actif });
      const rt = await api.admin.lieux.types.list();
      setTypes(rt.types);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Action impossible.');
    }
  };

  const toggleBrique = (b: string) => {
    setForm((f) => {
      const cur = f.briques ?? [];
      return { ...f, briques: cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b] };
    });
  };

  const typesActifs = types.filter((t) => t.actif);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      {dialogElement}

      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-ink">
            <MapPin className="h-6 w-6 text-tech-fg" />
            Lieux communaux
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Le référentiel unique des bâtiments de la commune. Un lieu s'y saisit une
            fois, puis se choisit dans toutes les briques.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setTypesOpen(true)}>
            <Tag className="mr-1.5 h-4 w-4" /> Types
          </Button>
          <Button variant="primary" onClick={ouvrirCreation}>
            <Plus className="mr-1.5 h-4 w-4" /> Nouveau lieu
          </Button>
        </div>
      </header>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}
      {msg && <Alert variant="success" className="mb-4">{msg}</Alert>}

      <label className="mb-4 flex items-center gap-2 text-sm text-ink-muted">
        <input
          type="checkbox"
          checked={inclureInactifs}
          onChange={(e) => setInclureInactifs(e.target.checked)}
          className="h-4 w-4 rounded border-border"
        />
        Afficher aussi les lieux désactivés
      </label>

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
        </div>
      ) : lieux.length === 0 ? (
        <Card className="p-8 text-center text-ink-muted">
          <Building2 className="mx-auto mb-3 h-10 w-10 opacity-40" />
          Aucun lieu pour l'instant. Créez le premier bâtiment de la commune.
        </Card>
      ) : (
        <div className="space-y-2">
          {lieux.map((l) => (
            <Card key={l.id} className={`p-4 ${l.actif ? '' : 'opacity-60'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{l.nom}</span>
                    {l.typeLibelle && <Badge variant="tech">{l.typeLibelle}</Badge>}
                    {l.louable && <Badge variant="neutral">Réservable</Badge>}
                    {l.categorieErp != null && (
                      <Badge variant="warning">
                        ERP {l.categorieErp}{l.typeErp ? `·${l.typeErp}` : ''}
                      </Badge>
                    )}
                    {!l.actif && <Badge variant="neutral">Désactivé</Badge>}
                  </div>
                  {(l.adresse || l.ville) && (
                    <p className="mt-1 text-sm text-ink-muted">
                      {[l.adresse, l.codePostal, l.ville].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {l.briques.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {l.briques.map((b) => (
                        <span
                          key={b}
                          className="inline-flex items-center rounded-md bg-surface/60 px-2 py-0.5 text-xs text-ink-muted"
                        >
                          {BRIQUE_LABEL[b] ?? b}
                        </span>
                      ))}
                    </div>
                  )}
                  {l.categorieErp != null && l.capaciteErp != null && (
                    <p className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
                      <Users className="h-3.5 w-3.5" /> Effectif public admissible : {l.capaciteErp}
                    </p>
                  )}
                  {/* S4-b : établissements qui occupent ce bâtiment */}
                  <button
                    type="button"
                    onClick={() => void toggleOccupants(l.id)}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-tech-fg hover:underline"
                  >
                    <Home className="h-3.5 w-3.5" />
                    {occupants[l.id] ? 'Masquer les occupants' : 'Ce bâtiment abrite…'}
                  </button>

                  {/* 9.2 — déclarer les clés depuis la fiche, sans notion de porte.
                      C'est le geste de mise en service : l'agent qui crée la
                      salle des fêtes saisit ses trois clés et s'en va. */}
                  <button
                    type="button"
                    onClick={() => void declarerCles(l)}
                    className="mt-2 ml-3 inline-flex items-center gap-1 text-xs text-tech-fg hover:underline"
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    Déclarer des clés
                  </button>
                  {(clesParLieu[l.id]?.length ?? 0) > 0 && (
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                      <span>
                        {clesParLieu[l.id]!.length} clé{clesParLieu[l.id]!.length > 1 ? 's' : ''} déclarée{clesParLieu[l.id]!.length > 1 ? 's' : ''}.
                      </span>
                      {/* Gérer les clés se fait dans la brique technique : on y
                          emmène plutôt que de dupliquer l'écran ici. */}
                      <a href="/service-technique/cles" className="text-tech-fg hover:underline">
                        Gérer les clés
                      </a>
                      <button
                        type="button"
                        onClick={() => void api.admin.lieux
                          .clesDuLieuPdf(l.id, `cles-${l.nom}.pdf`)
                          .catch(() => setError('Téléchargement impossible.'))}
                        className="text-tech-fg hover:underline"
                      >
                        Feuille pour la boîte à clés (PDF)
                      </button>
                    </p>
                  )}
                  {occupants[l.id] === 'loading' && (
                    <p className="mt-1 text-xs text-ink-muted">Chargement…</p>
                  )}
                  {Array.isArray(occupants[l.id]) && (
                    (occupants[l.id] as OccupantLieuDTO[]).length === 0 ? (
                      <p className="mt-1 text-xs text-ink-muted">
                        Aucun établissement rattaché pour l'instant.
                      </p>
                    ) : (
                      <ul className="mt-1 space-y-0.5">
                        {(occupants[l.id] as OccupantLieuDTO[]).map((o) => (
                          <li key={`${o.service}-${o.id}`} className="text-xs text-ink">
                            {o.nom} <span className="text-ink-muted">— {o.serviceLibelle}</span>
                          </li>
                        ))}
                      </ul>
                    )
                  )}
                  {Array.isArray(occupants[l.id]) && (occupations[l.id] ?? []).length > 0 && (
                    <div className="mt-1.5">
                      <p className="text-2xs font-medium uppercase tracking-wide text-ink-muted">
                        Occupé par la mairie
                      </p>
                      <ul className="mt-0.5 space-y-0.5">
                        {(occupations[l.id] ?? []).map((o) => (
                          <li key={o.id} className="text-xs text-ink">
                            {o.motif}
                            <span className="text-ink-muted">
                              {' '}— du {new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris' }).format(new Date(o.dateDebut + 'T00:00:00'))}
                              {' '}au {new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris' }).format(new Date(o.dateFin + 'T00:00:00'))}
                              {' '}(les salles rattachées ne sont pas réservables)
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(occupants[l.id]) && (contrats[l.id] ?? []).length > 0 && (
                    <div className="mt-1.5">
                      <p className="text-2xs font-medium uppercase tracking-wide text-ink-muted">
                        Contrats &amp; baux
                      </p>
                      <ul className="mt-0.5 space-y-0.5">
                        {(contrats[l.id] ?? []).map((c) => (
                          <li key={c.id} className="text-xs text-ink">
                            {c.libelle}
                            <span className="text-ink-muted">
                              {c.tiersNom ? ` — ${c.tiersNom}` : ''}
                              {/* La date affichée est la LIMITE DE DÉNONCIATION
                                  quand il y en a une : c'est elle qui compte,
                                  pas le terme. Voir mig 271. */}
                              {c.dateLimiteDenonciation
                                ? ` · à dénoncer avant le ${dateFr(c.dateLimiteDenonciation)}`
                                : c.dateFin ? ` · terme le ${dateFr(c.dateFin)}` : ' · sans terme'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(occupants[l.id]) && (contrats[l.id] ?? []).length === 0 && (
                    <p className="mt-1.5 text-2xs text-ink-subtle">
                      Aucun contrat rattaché à ce bâtiment.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="sm" onClick={() => ouvrirEdition(l)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void basculerActif(l)}>
                    {l.actif ? 'Désactiver' : 'Réactiver'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Dialog création / édition d'un lieu ── */}
      <Dialog
        open={editLieu !== null}
        onClose={() => setEditLieu(null)}
        title={editLieu === '' ? 'Nouveau lieu' : 'Modifier le lieu'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditLieu(null)}>Annuler</Button>
            <Button variant="primary" loading={saving} onClick={() => void enregistrer()}>
              Enregistrer
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Nom du lieu *</label>
            <Input
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="École du Centre"
            />
            <p className="mt-1 text-xs text-ink-muted">
              Saisi une seule fois. Il sera proposé en liste partout ailleurs.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">Type</label>
              <select
                value={form.typeId ?? ''}
                onChange={(e) => setForm({ ...form, typeId: e.target.value || null })}
                className={SELECT_CLS}
              >
                <option value="">— Sans type —</option>
                {typesActifs.map((t) => (
                  <option key={t.id} value={t.id}>{t.libelle}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">Surface (m²)</label>
              <Input
                type="number"
                value={form.surfaceM2 ?? ''}
                onChange={(e) => setForm({ ...form, surfaceM2: e.target.value ? Number(e.target.value) : null })}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1">
              <label className="mb-1 block text-sm font-medium text-ink">Adresse</label>
              <Input value={form.adresse ?? ''} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">Code postal</label>
              <Input value={form.codePostal ?? ''} onChange={(e) => setForm({ ...form, codePostal: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink">Ville</label>
              <Input value={form.ville ?? ''} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.louable ?? false}
              onChange={(e) => setForm({ ...form, louable: e.target.checked })}
              className="h-4 w-4 rounded border-border"
            />
            Ce lieu peut être réservé (salle des fêtes, salle de réunion…)
          </label>

          {/* ── Bloc ERP (déplié seulement si une catégorie est choisie) ── */}
          <div className="rounded-lg border border-border/60 p-3">
            <label className="mb-1 block text-sm font-medium text-ink">Catégorie ERP</label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={form.categorieErp ?? ''}
                onChange={(e) => setForm({ ...form, categorieErp: e.target.value ? Number(e.target.value) : null })}
                className={SELECT_CLS}
              >
                <option value="">— Pas un ERP —</option>
                {[1, 2, 3, 4, 5].map((c) => <option key={c} value={c}>Catégorie {c}</option>)}
              </select>
              {estErp && (
                <Input
                  value={form.typeErp ?? ''}
                  onChange={(e) => setForm({ ...form, typeErp: e.target.value })}
                  placeholder="Type ERP (R, L, N…)"
                />
              )}
            </div>
            {estErp && (
              <div className="mt-3">
                <label className="mb-1 block text-sm font-medium text-ink">
                  Effectif public admissible
                </label>
                <Input
                  type="number"
                  value={form.capaciteErp ?? ''}
                  onChange={(e) => setForm({ ...form, capaciteErp: e.target.value ? Number(e.target.value) : null })}
                />
                <p className="mt-1 text-xs text-ink-muted">
                  Effectif ERP réglementaire — distinct d'un éventuel agrément (crèche…).
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Briques gestionnaires</label>
            <p className="mb-2 text-xs text-ink-muted">
              Quelles briques utilisent ce lieu. Plusieurs possibles (une école porte
              cantine et centre de loisirs).
            </p>
            <div className="flex flex-wrap gap-2">
              {briques.map((b) => {
                const on = (form.briques ?? []).includes(b);
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => toggleBrique(b)}
                    className={`rounded-md border px-2.5 py-1 text-xs transition-all ${
                      on
                        ? 'border-tech bg-tech/15 text-tech-fg'
                        : 'border-border bg-surface/40 text-ink-muted hover:border-border-strong'
                    }`}
                  >
                    {BRIQUE_LABEL[b] ?? b}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Notes</label>
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={TEXTAREA_CLS}
              rows={2}
            />
          </div>
        </div>
      </Dialog>

      {/* ── Dialog gestion des types (bac à sable) ── */}
      <Dialog
        open={typesOpen}
        onClose={() => setTypesOpen(false)}
        title="Types de lieux"
        description="VPC propose des types ; vous les renommez, les désactivez ou en ajoutez librement."
        size="md"
      >
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={nouveauType}
              onChange={(e) => setNouveauType(e.target.value)}
              placeholder="Ajouter un type (ex. Halle, Presbytère…)"
              onKeyDown={(e) => { if (e.key === 'Enter') void creerType(); }}
            />
            <Button variant="primary" onClick={() => void creerType()}>Ajouter</Button>
          </div>
          <div className="space-y-1">
            {types.map((t) => (
              <div
                key={t.id}
                className={`flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 ${
                  t.actif ? '' : 'opacity-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ink">{t.libelle}</span>
                  {t.nbLieux > 0 && (
                    <span className="text-xs text-ink-muted">· {t.nbLieux} lieu{t.nbLieux > 1 ? 'x' : ''}</span>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => void basculerType(t)}>
                  {t.actif ? 'Désactiver' : 'Réactiver'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
